import { createReducer, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { User } from './user.model';
import * as UserActions from './user.actions';
import { QueryParams, queryParamsFromResult } from 'src/app/modules/core/store/models/query.model';

export const usersFeatureKey = 'users';

export interface State extends EntityState<User> {
  queryParams?: QueryParams;
  totalUserCount?: number;
  searchTerm?: string | null;
  selectedUserUId?: string | null;
  selectedUIds?: Set<string>;
  inverseSelected?: boolean;
  err?: Error;
  ui?: {
    showInviteForm?: boolean;
    showEditForm?: boolean;
    isInviting?: boolean;
    isSubmitting?: boolean;
  };
}

export const adapter: EntityAdapter<User> = createEntityAdapter<User>({
  selectId: (user: User) => user.uId
});

export const initialState: State = adapter.getInitialState({
  queryParams: {
    skip: 0,
    take: 10,
    totalCount: 0,
    sortField: 'DisplayName',
    sortDirection: 'Ascending',
    where: 'IsServiceAccount==false'
  },
  totalUserCount: 0,
  searchTerm: '',
  selectedUserUId: null,
  selectedUIds: new Set<string>(),
  err: null,
  ui: { showEditForm: false }
});

export const reducer = createReducer(
  initialState,
  on(UserActions.loadUsers, (state, action) => {
    let params = queryParamsFromResult(action.results);

    if (!params.where) {
      params.where = 'IsServiceAccount==false';
    } else {
      params.where += ' AND IsServiceAccount==false';
    }
    return adapter.setAll(action.results.data, {
      ...state,
      queryParams: params,
      selectedUserUId: null,
      err: null
    });
  }),
  on(UserActions.updateUser, (state, { user }) =>
    adapter.updateOne(
      {
        id: user.uId,
        changes: {
          ...user
        }
      },
      {
        ...state
      }
    )
  ),
  on(UserActions.setSelectedUserUId, (state, { uId }) => ({ ...state, selectedUserUId: uId })),
  on(UserActions.setSelectedUIds, (state, { selectedUIds }) => ({ ...state, selectedUIds })),
  on(UserActions.clearSelectedUIds, state => ({ ...state, selectedUIds: new Set<string>() })),
  on(UserActions.showInviteForm, (state, { showInviteForm }) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showInviteForm
      }
    };
  }),
  on(UserActions.showEditForm, (state, { showEditForm }) => {
    return {
      ...state,
      selectedUserUId: showEditForm ? state.selectedUserUId : null,
      ui: {
        ...state.ui,
        showEditForm
      }
    };
  }),
  on(UserActions.setIsInviting, (state, { isInviting }) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isInviting
      }
    };
  }),
  on(UserActions.setIsSubmitting, (state, { isSubmitting }) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isSubmitting
      }
    };
  }),
  on(UserActions.setTotalUserCount, (state, { totalUserCount }) => ({
    ...state,
    totalUserCount
  })),
  on(UserActions.setSearchTerm, (state, { searchTerm = '' }) => ({
    ...state,
    searchTerm
  }))
);

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const getQueryParams = (state: State) => state?.queryParams;

export const getSelectedUser = ({ selectedUserUId, entities }: State) => (!!selectedUserUId ? entities[selectedUserUId] : null);

export const getSelectedUIds = (state: State) => state?.selectedUIds ?? new Set<string>();

export const getShowInviteForm = (state: State) => state?.ui?.showInviteForm;

export const getShowEditForm = (state: State) => state?.ui?.showEditForm;

export const getIsInviting = (state: State) => state?.ui?.isInviting;

export const getIsSubmitting = (state: State) => state?.ui?.isSubmitting;

export const getError = (state: State) => state?.err?.message;

export const getTotalUserCount = (state: State) => state?.totalUserCount;

export const getSearchTerm = (state: State) => state?.searchTerm;
